import { getDirectoryHandleCaseInsensitive } from '@/features/project-disk/fs'
import { parseProjectsBinFromBytes, type ParseProjectsProgress } from '@/features/project-disk/parseProjectsBin'
import { syncLocalProjectResourcesToCloud } from '@/features/project-disk/syncLocalProjectResources'
import { requestNewProjectRemoteMeta } from '@/features/project-cloud/api'
import {
  projectOssSaveService,
  type ProjectOssSaveProgress,
} from '@/features/project-cloud/ossSave'
import {
  ensureMallNewForProject,
  PROJECT_MALL_NEW_FILE_NAME,
  readMallNewBin,
} from '@/features/project-mall/runtime'
import {
  parseVirtualResourcesJson,
  VIRTUAL_RESOURCES_FILE,
} from '@/features/virtual-resource/persistence'
import type { ResourceCategoryKey } from '@/features/virtual-resource/constants'
import type DataFileMap from '@/model/Resource/DataFileMap'
import type MainProject from '@/model/MainProject'

const PROJECT_BIN_NAMES = ['ProjectS.bin', 'projects.bin', 'Projects.bin', 'projectS.bin'] as const
const VIRTUAL_RESOURCE_FILE_NAMES = [VIRTUAL_RESOURCES_FILE, 'res.json'] as const

export type ImportLocalProjectToCloudResult = {
  guid: string
}

function isUserAbort(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError'
}

async function hasFile(dir: FileSystemDirectoryHandle, fileName: string): Promise<boolean> {
  try {
    await dir.getFileHandle(fileName)
    return true
  } catch {
    return false
  }
}

async function readFileBytes(dir: FileSystemDirectoryHandle, fileName: string): Promise<Uint8Array> {
  const handle = await dir.getFileHandle(fileName)
  const file = await handle.getFile()
  return new Uint8Array(await file.arrayBuffer())
}

async function findProjectBinName(dataDir: FileSystemDirectoryHandle): Promise<string | null> {
  for (const name of PROJECT_BIN_NAMES) {
    if (await hasFile(dataDir, name)) return name
  }
  return null
}

async function resolveProjectLayout(
  picked: FileSystemDirectoryHandle,
): Promise<{ root: FileSystemDirectoryHandle; data: FileSystemDirectoryHandle; binFileName: string }> {
  try {
    const data = await getDirectoryHandleCaseInsensitive(picked, 'Data')
    const binFileName = await findProjectBinName(data)
    if (binFileName) return { root: picked, data, binFileName }
  } catch {
    /* 选中目录不是工程根 */
  }
  const binFileName = await findProjectBinName(picked)
  if (binFileName) {
    throw new Error('请选择工程根目录（需包含 Data 以及 Graphics / audio）')
  }
  throw new Error('未找到工程主包，请选择包含 Data/ProjectS.bin 的工程根目录')
}

async function readLocalVirtualResourceRoots(
  dataDir: FileSystemDirectoryHandle,
): Promise<Record<ResourceCategoryKey, DataFileMap | null>> {
  for (const fileName of VIRTUAL_RESOURCE_FILE_NAMES) {
    try {
      const handle = await dataDir.getFileHandle(fileName)
      const text = await (await handle.getFile()).text()
      return parseVirtualResourcesJson(text)
    } catch {
      /* 尝试下一个文件名 */
    }
  }
  return parseVirtualResourcesJson('')
}

/**
 * 选择本地工程目录，申请新的云端 guid，按现有 OSS 保存链路上传后返回 guid。
 * 用户取消选择时返回 null。本地目录只读一次，不保留磁盘句柄。
 */
export async function importLocalProjectToCloud(params: {
  uid: number
  sessionId: string
  onParseProgress?: ParseProjectsProgress
  onResourceProgress?: (percent: number, detail: string) => void
  onSaveProgress?: (progress: ProjectOssSaveProgress) => void
}): Promise<ImportLocalProjectToCloudResult | null> {
  const picker = window.showDirectoryPicker
  if (typeof picker !== 'function') {
    throw new Error('当前浏览器不支持打开本地文件夹')
  }

  let picked: FileSystemDirectoryHandle
  try {
    picked = await picker.call(window, { id: 'avgmaker-local-project', mode: 'read' })
  } catch (error) {
    if (isUserAbort(error)) return null
    throw error
  }

  const layout = await resolveProjectLayout(picked)
  const project = await parseProjectsBinFromBytes(
    await readFileBytes(layout.data, layout.binFileName),
    params.onParseProgress,
  )
  try {
    const mallBytes = await readFileBytes(layout.data, PROJECT_MALL_NEW_FILE_NAME)
    readMallNewBin(mallBytes, project)
  } catch {
    ensureMallNewForProject(project)
  }
  let virtualResourceRoots = await readLocalVirtualResourceRoots(layout.data)

  const remoteMeta = await requestNewProjectRemoteMeta({
    sessionId: params.sessionId,
    projName: project.storyTree.ProjectName?.trim() || picked.name || '未命名工程',
  })
  project.projectHeader.ID = remoteMeta.guid
  project.projectHeader.Time = remoteMeta.createTime

  virtualResourceRoots = await syncLocalProjectResourcesToCloud({
    projectRoot: layout.root,
    project,
    sessionId: params.sessionId,
    currentRoots: virtualResourceRoots,
    onProgress: params.onResourceProgress,
  })

  await projectOssSaveService.save({
    project,
    virtualResourceRoots,
    uid: params.uid,
    onProgress: params.onSaveProgress,
  })
  return { guid: remoteMeta.guid }
}
